import { AxiosError } from 'axios'
import { AuthModel } from './_models'
import { reauthWithRefreshToken } from './_requests';

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
const getAuth = (): AuthModel | undefined => {
  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel
    if (auth) {
      return auth
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
  }
}

const setAuth = (auth: AuthModel) => {
  if (!localStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(auth)
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
  }
}

const removeAuth = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}

export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = 'application/json'
  axios.defaults.withCredentials = true;

  // Request interceptor: Append bearer token with every request
  axios.interceptors.request.use(
    (config: { headers: { Authorization: string } }) => {
      const auth = getAuth()
      if (auth && auth.access_token) {
        config.headers.Authorization = `Bearer ${auth.access_token}`
      }

      return config
    },
    (err: any) => Promise.reject(err)
  )

  // Response interceptor: Handle unauthorized errors for logged in users
  axios.interceptors.response.use(
    (response: any) => response,
    async (error: AxiosError) => {
      const config = error.config;
      // Handle 401 unauthorized responses
      if (error.response && error.response.status === 401 && !(config as any).triedRefresh) {
        (config as any).triedRefresh = true;
        const auth = getAuth();

        if (auth && auth.access_token && auth.refresh_token) {
          // Try getting a new access token using the refresh token
          try {
            const { data: newAuth } = await reauthWithRefreshToken(auth.refresh_token);

            if (newAuth && newAuth.access_token) {
              setAuth(newAuth)

              config.headers = {
                ...config.headers,
                Authorization: `Bearer ${auth.access_token}`
              }

            }
            return axios(config);
          } catch (_error) {
            removeAuth();
            return Promise.reject(_error);
          }

          // User is already logged in, but is not authorized.
          // So, we prompt the user to reenter their credentials to get a new access token
        }
      }
      return Promise.reject(error);
    }
  )
}

export { getAuth, setAuth, removeAuth, AUTH_LOCAL_STORAGE_KEY }
